import * as _ from 'lodash';
import { registerFormatter } from '@integro/slickg';
import type { Formatter } from 'slickgrid';
import parseISO from 'date-fns/parseISO';
import { romanize } from '@integro/gettext';

interface FormatterEx<T = any> extends Formatter<T> {
  getCellValue?: Formatter<T>;
  skipFilter?: boolean;
  filterFormattedData?: boolean;
  filterRawData?: boolean;
  skipExport?: boolean;
  exportRawData?: boolean;
  exportDataFormatter?: FormatterEx<T>;
}

export function registerFormatters() {
  registerFormatter('dunningLetterBadges', dunningLetterBadges);
  registerFormatter('overdueHighlight', overdueHighlight);
  registerFormatter('routeLink', routeLink);
  registerFormatter('gettext', gettext);
  registerFormatter('user', user);
  registerFormatter('number', number);
  registerFormatter('checkmark', checkmark);
}

function dunningLetterBadges<T>() {
  const romanizeMonths = false;
  const statusToVariant: Dictionary<string> = {
    sent: 'warning',
    delivered: 'success',
    error: 'danger',
  };
  const defaultVariant = 'secondary';

  function $text(data: Dictionary<any>) {
    if (data && data['created_at']) {
      const d = parseISO(data['created_at']),
        monthNum = d.getMonth() + 1,
        month = romanizeMonths ? romanize(monthNum) : monthNum;

      return `${month}.${d.getDate()}`;
    }
    return '';
  }

  function $variant(data: Dictionary<any>) {
    let result;

    if (data && data['status']) {
      result = statusToVariant[data['status']];
    }

    return result || defaultVariant;
  }

  const formatter: FormatterEx<T> = (
    row,
    cell,
    value,
    columnDef,
    dataContext,
    grid,
  ) => {
    if (!value || value.length === 0) {
      return '';
    }
    return _.map(value, (val, i) => {
      const variant = $variant(val),
        refnum = val.reference_number,
        id = val.id,
        text = $text(val);

      return (
        `<a href="#" title="${refnum}" data-value="${id}" ` +
        `class="badge rounded-pill text-bg-${variant}">${text}</a>`
      );
    }).join(' ');
  };

  const getCellValue: FormatterEx<T> = (
    row,
    cell,
    value,
    columnDef,
    dataContext,
    grid,
  ) => {
    if (!value || value.length === 0) {
      return '';
    }
    return _.map(value, (val, i) => $text(val)).join(' ');
  };

  const exportDataFormatter: FormatterEx<T> = (
    row,
    cell,
    value,
    columnDef,
    dataContext,
  ) => {
    if (!value || value.length === 0) {
      return '';
    }
    return _.map(value, (val, i) => {
      const refnum = val.reference_number,
        text = $text(val);

      return `${text} [${refnum}]`;
    }).join(', ');
  };

  _.set(formatter, 'getCellValue', getCellValue);
  _.set(formatter, 'exportDataFormatter', exportDataFormatter);

  return formatter;
}

function overdueHighlight() {
  function getClassName(value: any) {
    if (_.isNumber(value) && value > 0) {
      const level = value < 35 ? 1 : value < 50 ? 2 : value < 65 ? 3 : 4;
      return `overdue highlight-${level}`;
    }
  }

  const overdueHighlightFormatter: FormatterEx<any> = (
    row,
    cell,
    value,
    columnDef,
    dataContext,
  ) => {
    if (value == null) {
      return '';
    }

    const className = getClassName(value) || '';

    return `<span class="${className}">${value}</span>`;
  };

  _.set(overdueHighlightFormatter, 'exportRawData', true);
  _.set(overdueHighlightFormatter, 'filterRawData', true);

  return overdueHighlightFormatter;
}

function routeLink(opts: {
  to: string;
  params: Dictionary<string>;
  innerHTML?: string;
  exportInnerHTML?: boolean;
  navigateOnIconClick?: boolean;
  target?: string;
}) {
  const exportInnerHTML = _.isBoolean(opts.exportInnerHTML)
    ? opts.exportInnerHTML
    : false;

  const icon =
    '<i class="fa fa-external-link ms-1 text-muted" aria-hidden="true"></i>';

  const getParams = (data: object) => {
    const result: Dictionary<any> = {};

    for (const [k, v] of Object.entries(opts.params)) {
      result[k] = _.get(data, v, null);
      if (result[k] === null) {
        return;
      }
    }

    return result;
  };

  const router = (window as any).$app?.router;

  const routeLinkFormatter: FormatterEx<any> = (
    row,
    cell,
    value,
    columnDef,
    dataContext,
  ) => {
    if (value == null) {
      return '';
    }

    let params = getParams(dataContext);

    if (!params && _.isObject(value)) {
      params = getParams(value);
    }

    if (!params || !router) {
      return opts.innerHTML || value;
    }

    try {
      const resolved = router.resolve({ name: `app.${opts.to}`, params });
      if (!resolved.matched || resolved.matched.length === 0) {
        return opts.innerHTML || value;
      }
      const url = resolved.href;

      let extras = '';
      if (opts.target) {
        extras += ` target="${opts.target}"`;
      }

      const content = opts.innerHTML || value;

      if (opts.navigateOnIconClick) {
        return `<a href="${url}" class="link-dark" ${extras.trim()}>${content}</a> <a href="${url}" ${extras.trim()}>${icon}</a>`;
      }

      return `<a href="${url}" ${extras.trim()}>${content}</a>`;
    } catch (e) {
      console.error(e);
      return opts.innerHTML || value;
    }
  };

  const exportDataFormatter: FormatterEx<any> = (
    row,
    cell,
    value,
    columnDef,
    dataContext,
  ) => {
    if (value == null) {
      return '';
    }

    if (!exportInnerHTML) {
      return value;
    }

    return opts.innerHTML || value;
  };

  _.set(routeLinkFormatter, 'exportDataFormatter', exportDataFormatter);

  return routeLinkFormatter;
}

function gettext(opts: { context?: string; conversion?: string | string[] }) {
  const result = <FormatterEx<any>>(
    function gettextFormatter(row, cell, value, columnDef, dataContext) {
      if (value == null) {
        return '';
      }
      const translate = (window as any).$translate;
      if (translate) {
        return translate.translate({ msgid: value, ...opts });
      }
      return value;
    }
  );

  _.set(result, 'filterFormattedData', true);
  return result;
}

function user() {
  const users = (window as any).$app?.users;

  return <FormatterEx<any>>(
    function userFormatter(row, cell, value, columnDef, dataContext) {
      if (value == null) {
        return '';
      }
      if (users) {
        return users.getUserDisplayName(value);
      }
      return value;
    }
  );
}

function number() {
  const numberFormatter: FormatterEx<any> = (
    row,
    cell,
    value,
    columnDef,
    dataContext,
  ) => {
    if (value == null) {
      return '';
    }
    const filter = (window as any).$app?.i18n?.filters?.number;
    if (filter) {
      return filter(value);
    }
    return value;
  };

  _.set(numberFormatter, 'exportRawData', true);

  return numberFormatter;
}

const checkmark_true =
  '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12"><path fill="currentColor" d="M9.765 3.205a.75.75 0 0 1 .03 1.06l-4.25 4.5a.75.75 0 0 1-1.075.015L2.22 6.53a.75.75 0 0 1 1.06-1.06l1.705 1.704l3.72-3.939a.75.75 0 0 1 1.06-.03Z"/></svg>';

function checkmark() {
  const checkmarkFormatter: FormatterEx<any> = (
    row,
    cell,
    value,
    columnDef,
    dataContext,
  ) => {
    if (value === true || value === 1) {
      return checkmark_true;
    }
    return '';
  };

  _.set(checkmarkFormatter, 'exportRawData', true);

  return checkmarkFormatter;
}
